import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  Shield, 
  Brain, 
  AlertTriangle,
  CheckCircle2,
  Scale,
  FileWarning,
  Globe,
  Building2,
  Info,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Lock,
  Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AiNoticeRequiredPage() {
  const navigate = useNavigate();
  const { token, updateUser, hasAcknowledgedAiNotice } = useAuth();
  const [loading, setLoading] = useState(true);
  const [acknowledging, setAcknowledging] = useState(false);
  const [aiNotice, setAiNotice] = useState(null);
  const [hasReadNotice, setHasReadNotice] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    dataSent: true,
    openaiPolicy: false,
    privilege: false,
    compliance: false,
    security: false,
    recommendations: false
  });

  useEffect(() => {
    // If already acknowledged, redirect to dashboard
    if (hasAcknowledgedAiNotice()) {
      navigate('/dashboard', { replace: true });
      return;
    }
    fetchAiNotice();
  }, []);

  const fetchAiNotice = async () => {
    try {
      const res = await fetch(`${API_URL}/api/security/ai-privacy-notice`);
      if (res.ok) {
        setAiNotice(await res.json());
      }
    } catch (error) {
      console.error('Error fetching AI notice:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAcknowledge = async () => {
    if (!hasReadNotice) {
      toast.error('Please confirm that you have read and understood the notice');
      return;
    }

    setAcknowledging(true);
    try {
      const res = await fetch(`${API_URL}/api/security/acknowledge-ai-notice`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        // Update user in auth context
        if (data.user) {
          updateUser(data.user);
        }
        toast.success('Thank you for reviewing our AI privacy notice');
        navigate('/dashboard', { replace: true });
      } else {
        throw new Error('Failed to acknowledge');
      }
    } catch (error) {
      toast.error('Failed to save acknowledgment. Please try again.');
    } finally {
      setAcknowledging(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const CollapsibleSection = ({ id, icon: Icon, title, children, iconColor = "text-navy", defaultOpen = false }) => (
    <div className="border rounded-lg overflow-hidden">
      <button
        onClick={() => toggleSection(id)}
        className="w-full flex items-center justify-between p-4 bg-muted/30 hover:bg-muted/50 transition-colors text-left"
      >
        <div className="flex items-center gap-3">
          <Icon className={`w-5 h-5 ${iconColor}`} />
          <span className="font-medium">{title}</span>
        </div>
        {expandedSections[id] ? (
          <ChevronUp className="w-5 h-5 text-muted-foreground" />
        ) : (
          <ChevronDown className="w-5 h-5 text-muted-foreground" />
        )}
      </button>
      {expandedSections[id] && (
        <div className="p-4 border-t bg-white">
          {children}
        </div>
      )}
    </div>
  );

  const ExternalLinkButton = ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 text-sm text-bronze hover:underline"
    >
      {children}
      <ExternalLink className="w-3 h-3" />
    </a>
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse">
          <Shield className="w-12 h-12 text-navy/20 mx-auto mb-4" />
          <p className="text-muted-foreground">Loading security information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-background" data-testid="ai-notice-required-page">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-md bg-navy flex items-center justify-center">
                <Scale className="w-5 h-5 text-white" />
              </div>
              <span className="font-heading text-xl font-bold">Legal AIDynamics</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Important Notice Banner */}
        <div className="bg-amber-100 border border-amber-300 rounded-lg p-6 mb-8">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-amber-200 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-6 h-6 text-amber-700" />
            </div>
            <div>
              <h1 className="text-xl font-heading font-bold text-amber-900 mb-2">
                AI Privacy Notice Review Required
              </h1>
              <p className="text-amber-800">
                Before you can use Legal AIDynamics' features, you must review and acknowledge our AI Data Handling & Privacy Notice. 
                This ensures you understand how your confidential legal documents are processed when using AI-powered features.
              </p>
            </div>
          </div>
        </div>

        {/* Main Notice Card */}
        <Card className="border-2 border-amber-200 shadow-lg">
          <CardHeader className="bg-amber-50 border-b border-amber-200">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-amber-100 flex items-center justify-center">
                <Brain className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <CardTitle className="text-xl">AI Data Handling & Privacy Notice</CardTitle>
                <CardDescription>
                  Version {aiNotice?.version || '2.0'} • Last updated: {aiNotice?.last_updated || 'December 2025'}
                </CardDescription>
              </div>
            </div>
          </CardHeader>

          <CardContent className="p-6 space-y-6">
            {/* Summary */}
            <div className="bg-white rounded-lg p-4 border-2 border-amber-200">
              <p className="text-muted-foreground leading-relaxed">
                {aiNotice?.summary || 'Loading...'}
              </p>
            </div>

            {/* Collapsible Sections */}
            <div className="space-y-3">
              {/* What Data Is Sent */}
              <CollapsibleSection id="dataSent" icon={FileWarning} title="What Data Is Sent to AI" iconColor="text-blue-600">
                {aiNotice?.data_sent_to_ai && (
                  <div className="space-y-4">
                    <p className="text-sm text-muted-foreground">{aiNotice.data_sent_to_ai.description}</p>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="bg-red-50 p-4 rounded-lg border border-red-100">
                        <h5 className="font-medium text-red-800 mb-2">Data Sent:</h5>
                        <ul className="text-sm text-red-700 space-y-1">
                          {aiNotice.data_sent_to_ai.items?.map((item, i) => (
                            <li key={i} className="flex items-start gap-2">
                              <span className="text-red-500 mt-1">•</span>
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      <div className="bg-green-50 p-4 rounded-lg border border-green-100">
                        <h5 className="font-medium text-green-800 mb-2">NOT Sent:</h5>
                        <ul className="text-sm text-green-700 space-y-1">
                          {aiNotice.data_sent_to_ai.not_sent?.map((item, i) => (
                            <li key={i} className="flex items-start gap-2">
                              <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
              </CollapsibleSection>

              {/* OpenAI Data Policy */}
              <CollapsibleSection id="openaiPolicy" icon={Globe} title="OpenAI Data Retention Policy" iconColor="text-purple-600">
                {aiNotice?.openai_data_policy && (
                  <div className="space-y-4">
                    <p className="text-sm text-muted-foreground">{aiNotice.openai_data_policy.description}</p>
                    
                    <ul className="text-sm space-y-2">
                      {aiNotice.openai_data_policy.items?.map((item, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                    
                    <div className="pt-2 border-t">
                      <p className="text-xs text-muted-foreground mb-2">Official OpenAI Resources:</p>
                      <div className="flex flex-wrap gap-3">
                        {aiNotice.openai_data_policy.links?.map((link, i) => (
                          <ExternalLinkButton key={i} href={link.url}>
                            {link.text}
                          </ExternalLinkButton>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </CollapsibleSection>

              {/* Attorney-Client Privilege */}
              <CollapsibleSection id="privilege" icon={Scale} title="Attorney-Client Privilege Considerations" iconColor="text-amber-600">
                {aiNotice?.attorney_client_privilege && (
                  <div className="space-y-4">
                    <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                      <p className="text-sm text-amber-800 font-medium mb-2">⚠️ Important Legal Considerations</p>
                      <p className="text-sm text-amber-700">{aiNotice.attorney_client_privilege.description}</p>
                    </div>
                    
                    <div>
                      <h5 className="font-medium mb-2">Key Points:</h5>
                      <ul className="text-sm space-y-2">
                        {aiNotice.attorney_client_privilege.items?.map((item, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div>
                      <h5 className="font-medium mb-2">Recommendations:</h5>
                      <ul className="text-sm space-y-2">
                        {aiNotice.attorney_client_privilege.recommendations?.map((item, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div className="pt-2 border-t">
                      <p className="text-xs text-muted-foreground mb-2">ABA Resources:</p>
                      <div className="flex flex-wrap gap-3">
                        {aiNotice.attorney_client_privilege.links?.map((link, i) => (
                          <ExternalLinkButton key={i} href={link.url}>
                            {link.text}
                          </ExternalLinkButton>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </CollapsibleSection>

              {/* Regulatory Compliance */}
              <CollapsibleSection id="compliance" icon={Building2} title="Regulatory Compliance Considerations" iconColor="text-indigo-600">
                {aiNotice?.compliance_considerations && (
                  <div className="space-y-4">
                    <p className="text-sm text-muted-foreground">{aiNotice.compliance_considerations.description}</p>
                    
                    <div className="space-y-3">
                      {aiNotice.compliance_considerations.regulations?.map((reg, i) => (
                        <div key={i} className="bg-muted/30 p-4 rounded-lg">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge variant="outline" className="font-semibold">{reg.name}</Badge>
                            <span className="text-xs text-muted-foreground">— {reg.applies_to}</span>
                          </div>
                          <p className="text-sm text-muted-foreground">{reg.consideration}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CollapsibleSection>

              {/* Our Security Measures */}
              <CollapsibleSection id="security" icon={Lock} title="Our Security Measures" iconColor="text-green-600">
                {aiNotice?.security_measures && (
                  <div className="space-y-4">
                    <p className="text-sm text-muted-foreground">{aiNotice.security_measures.description}</p>
                    
                    <div className="grid md:grid-cols-2 gap-2">
                      {aiNotice.security_measures.items?.map((item, i) => (
                        <div key={i} className="flex items-center gap-2 p-2 bg-green-50 rounded text-sm">
                          <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                          {item}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CollapsibleSection>

              {/* Best Practices */}
              <CollapsibleSection id="recommendations" icon={Info} title="Best Practices for Confidential Documents" iconColor="text-navy">
                {aiNotice?.recommendations && (
                  <div>
                    <ul className="text-sm space-y-2">
                      {aiNotice.recommendations.items?.map((item, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-bronze mt-0.5 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </CollapsibleSection>
            </div>

            {/* Acknowledgment Section */}
            <div className="border-t-2 border-amber-200 pt-6 mt-6">
              <div className="bg-navy/5 rounded-lg p-6">
                <div className="flex items-start space-x-3 mb-6">
                  <Checkbox
                    id="acknowledge"
                    checked={hasReadNotice}
                    onCheckedChange={setHasReadNotice}
                    className="mt-1"
                  />
                  <Label htmlFor="acknowledge" className="text-sm leading-relaxed cursor-pointer">
                    <span className="font-semibold">I have read and understood the AI Data Handling & Privacy Notice above.</span>
                    <br />
                    <span className="text-muted-foreground">
                      I understand how my documents will be processed when using AI-powered features, and I acknowledge the 
                      attorney-client privilege and compliance considerations relevant to my practice.
                    </span>
                  </Label>
                </div>

                <Button
                  onClick={handleAcknowledge}
                  disabled={!hasReadNotice || acknowledging}
                  className="w-full bg-navy hover:bg-navy-light text-lg py-6"
                  size="lg"
                >
                  {acknowledging ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-5 h-5 mr-2" />
                      I Have Read and Understand This Notice
                    </>
                  )}
                </Button>

                <p className="text-xs text-center text-muted-foreground mt-4">
                  You can review these disclosures at any time in Security & Privacy settings.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
